import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../utils/apiConfig';
import {
    Search,
    RefreshCw,
    Eye,
    Edit,
    Trash2,
    Plus,
    Users,
    Wallet,
    Clock,
    CheckCircle,
    AlertCircle,
    Copy,
    ExternalLink,
    ArrowLeft
} from 'lucide-react';

const WalletServiceTypesPage = () => {
    const [walletServiceTypes, setWalletServiceTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [limit] = useState(10);

    const navigate = useNavigate();

    // Fetch wallet service types
    const fetchWalletServiceTypes = async (page = 1, search = '') => {
        try {
            setLoading(true);
            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                ...(search && { search })
            });

            const response = await fetch(
                `${API_BASE_URL}/wallet-service-types/list?${queryParams}`,
                {
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setWalletServiceTypes(data.data || []);
                setTotalPages(data.pagination?.pages || 1);
                setTotalCount(data.pagination?.total || 0);
                setError(null);
            } else {
                throw new Error(data.message || 'Failed to fetch wallet service types');
            }
        } catch (err) {
            console.error('Error fetching wallet service types:', err);
            setError(err.message);
            setWalletServiceTypes([]);
        } finally {
            setLoading(false);
        }
    };

    // Initial load
    useEffect(() => {
        fetchWalletServiceTypes(currentPage, searchTerm);
    }, [currentPage, searchTerm]);

    // Refresh data
    const refreshData = async () => {
        setIsRefreshing(true);
        await fetchWalletServiceTypes(currentPage, searchTerm);
        setTimeout(() => setIsRefreshing(false), 500);
    };

    // Handle search
    const handleSearch = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        setCurrentPage(1); // Reset to first page when searching
    };

    // Handle pagination
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    // Copy to clipboard
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        // You could add a toast notification here
    };

    // Get status color
    const getStatusColor = (isActive) => {
        return isActive
            ? 'bg-green-100 text-green-700 border-green-300'
            : 'bg-gray-100 text-gray-600 border-gray-300';
    };

    // Get status text
    const getStatusText = (isActive) => {
        return isActive ? 'ACTIVE' : 'INACTIVE';
    };

    if (loading && !isRefreshing) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
                    <p className="text-gray-600">Loading wallet service types...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="text-gray-600 hover:text-gray-800 transition-colors p-2 rounded-lg hover:bg-gray-100"
                                title="Back to Dashboard"
                            >
                                <ArrowLeft className="w-6 h-6" />
                            </button>
                            <div>
                                <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
                                    Wallet Service Types
                                </h1>
                                <p className="text-gray-600 text-sm md:text-base">
                                    Manage user wallet services and view wallet information
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row items-center gap-3">
                            <button
                                onClick={refreshData}
                                disabled={isRefreshing}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-300 flex items-center space-x-2 shadow-lg hover:shadow-blue-500/25 disabled:opacity-50"
                            >
                                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                                <span>Refresh</span>
                            </button>
                        </div>
                    </div>

                    {/* Search and Stats */}
                    {/* <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-6">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div className="relative flex-1 max-w-md">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search by name, email, wallet address..."
                                    value={searchTerm}
                                    onChange={handleSearch}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                    </div> */}
                </div>

                {/* Error State */}
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <div className="flex items-center space-x-2">
                            <AlertCircle className="w-5 h-5 text-red-600" />
                            <p className="text-red-700">{error}</p>
                        </div>
                    </div>
                )}

                {/* Wallet Service Types List */}
                <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                    {walletServiceTypes.length > 0 ? (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 border-b border-gray-200">
                                        <tr>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                User Information
                                            </th>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Wallets
                                            </th>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Balance
                                            </th>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Created
                                            </th>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {walletServiceTypes.map((wst) => (
                                            <tr key={wst._id} className="hover:bg-gray-50 transition-colors duration-200">
                                                {/* User Information */}
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center space-x-3">
                                                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                                            <Users className="w-5 h-5 text-blue-600" />
                                                        </div>
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-800">
                                                                {wst.userInfo.fullName || 'N/A'}
                                                            </div>
                                                            <div className="text-sm text-gray-600">{wst.userInfo.email}</div>
                                                            {wst.userInfo.phone && (
                                                                <div className="text-xs text-gray-500">{wst.userInfo.phone}</div>
                                                            )}
                                                            <div className="text-xs text-gray-500">ID: {wst.userId}</div>
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* Wallets */}
                                                <td className="px-6 py-4">
                                                    <div className="space-y-2">
                                                        {wst.wallets && wst.wallets.length > 0 ? (
                                                            wst.wallets.map((wallet, index) => (
                                                                <div key={index} className="bg-gray-50 rounded-lg p-3">
                                                                    <div className="flex items-center justify-between mb-1">
                                                                        <span className="text-xs font-medium text-gray-700">
                                                                            {wallet.walletNetwork}
                                                                        </span>
                                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${wallet.isActive ? 'bg-green-100 text-green-700 border-green-300' : 'bg-gray-100 text-gray-600 border-gray-300'
                                                                            }`}>
                                                                            {wallet.isActive ? 'Active' : 'Inactive'}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex items-center space-x-2">
                                                                        <code className="text-xs text-gray-600 bg-white px-2 py-1 rounded border font-mono">
                                                                            {wallet.walletAddress ? `${wallet.walletAddress.slice(0, 8)}...${wallet.walletAddress.slice(-6)}` : 'N/A'}
                                                                        </code>
                                                                        {wallet.walletAddress && (
                                                                            <button
                                                                                onClick={() => copyToClipboard(wallet.walletAddress)}
                                                                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                                                                title="Copy address"
                                                                            >
                                                                                <Copy className="w-3 h-3" />
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                    {wallet.walletGeneratedAt && (
                                                                        <div className="text-xs text-gray-500 mt-1">
                                                                            <Clock className="w-3 h-3 inline mr-1" />
                                                                            {new Date(wallet.walletGeneratedAt).toLocaleDateString()}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div className="text-sm text-gray-500 italic">No wallets</div>
                                                        )}
                                                    </div>
                                                </td>

                                                {/* Balance */}
                                                <td className="px-6 py-4">
                                                    <div className="space-y-1">
                                                        {wst.wallets && wst.wallets.length > 0 ? (
                                                            wst.wallets.map((wallet, index) => (
                                                                <div key={index} className="text-sm">
                                                                    <div className="font-medium text-gray-800">
                                                                        {wallet.totalDepositAmount?.toFixed(2) || '0.00'} USDT
                                                                    </div>
                                                                    <div className="text-xs text-gray-500">
                                                                        Deposits: {wallet.totalDeposits || 0}
                                                                    </div>
                                                                    {wallet.lastDepositAmount > 0 && (
                                                                        <div className="text-xs text-green-600">
                                                                            Last: +{wallet.lastDepositAmount} USDT
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div className="text-sm text-gray-500">-</div>
                                                        )}
                                                    </div>
                                                </td>

                                                {/* Status */}
                                                <td className="px-6 py-4">
                                                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(wst.isActive)}`}>
                                                        {getStatusText(wst.isActive)}
                                                    </span>
                                                </td>

                                                {/* Created */}
                                                <td className="px-6 py-4">
                                                    <div className="text-sm text-gray-600">
                                                        {new Date(wst.createdAt).toLocaleDateString()}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {new Date(wst.createdAt).toLocaleTimeString()}
                                                    </div>
                                                </td>

                                                {/* Actions */}
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center space-x-2">
                                                        <button
                                                            onClick={() => navigate(`/wallet-service-types/${wst._id}`)}
                                                            className="text-blue-600 hover:text-blue-700 transition-colors"
                                                            title="View details"
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm text-gray-700">
                                            Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, totalCount)} of {totalCount} results
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                                className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Previous
                                            </button>

                                            {[...Array(totalPages)].map((_, i) => {
                                                const page = i + 1;
                                                if (
                                                    page === 1 ||
                                                    page === totalPages ||
                                                    (page >= currentPage - 2 && page <= currentPage + 2)
                                                ) {
                                                    return (
                                                        <button
                                                            key={page}
                                                            onClick={() => handlePageChange(page)}
                                                            className={`px-3 py-1 border rounded-lg text-sm font-medium ${currentPage === page
                                                                ? 'bg-blue-600 text-white border-blue-600'
                                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                                                }`}
                                                        >
                                                            {page}
                                                        </button>
                                                    );
                                                } else if (
                                                    page === currentPage - 3 ||
                                                    page === currentPage + 3
                                                ) {
                                                    return <span key={page} className="text-gray-500">...</span>;
                                                }
                                                return null;
                                            })}

                                            <button
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                                className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Next
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="p-12 text-center">
                            <Wallet className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                            <h3 className="text-lg font-medium text-gray-800 mb-2">No Wallet Service Types Found</h3>
                            <p className="text-gray-600 mb-6">
                                {searchTerm ? 'No results match your search criteria.' : 'No wallet service types have been created yet.'}
                            </p>
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="text-blue-600 hover:text-blue-700 font-medium"
                                >
                                    Clear search
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WalletServiceTypesPage;